import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import 'draft-js/dist/Draft.css';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';

import TaboolaFieldsAllMetric from './TaboolaFieldsAllMetric';
import TaboolaFieldsSelected from './TaboolaFieldsSelected';
import TaboolaFieldsCustomConvertions from './TaboolaFieldsCustomConvertions';

import useGlobalStatus from '../../../hook/useGlobalStatus';
import useUtils from '../../../hook/useUtils';

import { optionalFields, preselectedFields } from '../../../utils/TaboolaFieldsDefined';
import { BoxBorderGrey, CustomFormLabel } from '../../customComponent';

import { PagesOutlinedIcon } from '../../../constants/images';

import theme from '../../../theme';


const maxFieldsIndexNumber = 26;

const getCustomFields = (currentFields) => {
  const customFields = _.filter(currentFields, f => f.type === 'CUSTOM' && f.cid === null);
  return [{
    category: 'custom',
    name: '自定義指標',
    child: customFields
  }]
}

const TaboolaFieldsData = ({
  selectedAdAccounts,
  excelSheetSelected,
  allOptionFields,
  setAllOptionFields,
  selectedOptionFields,
  setSelectedOptionFields,
  handleModalDetail,
  handleCustomData,
  fetchData,
  inputErrorMsg,
  downloadInputErrorMsg,
  origiCustomData,
  setOrigiCustomData,
  setModalName,
  setIsLoading,
  scrollToAnchor,
  updateSortFields,
  setUpdateSortFields,
}) => {
  const { currentActiveData, entryPath, setEntryPath } = useGlobalStatus();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterOptionFields, setFilterOptionFields] = useState([]);
  const [currenOptionFields, setCurrenOptionFields] = useState(null);
  const [transformExpressToTw] = useUtils();
  const currentDataWithCustomFields = getCustomFields(currentActiveData.fields);

  useEffect(() => {
    if (entryPath.includes('edit')) {
      const sortFields = currentActiveData.fields.sort((a, b) => {
        if (a.indexNumber === null) return 1;
        if (b.indexNumber === null) return -1;
        return a.indexNumber - b.indexNumber;
      })
      setSelectedOptionFields(sortFields);
    } else {
      setSelectedOptionFields(preselectedFields);
    }
  }, [entryPath, currentActiveData])

  //更新顯示欄位
  useEffect(() => {
    setCurrenOptionFields(null);

    let allDisplayOptionFields = [];

    if (!selectedAdAccounts.length || entryPath.includes('create')) {
      allDisplayOptionFields = optionalFields;

    } else {
      allDisplayOptionFields = currentDataWithCustomFields.concat(optionalFields);
    }
    setAllOptionFields(allDisplayOptionFields);

  }, [optionalFields.length, selectedAdAccounts])

  useEffect(() => {
    let fields;

    if (searchKeyword) {
      fields = allOptionFields.map((el) => {
        const filteredChild = el.child.filter((elem) =>
          elem.name.toLowerCase().includes(searchKeyword.toLowerCase())
        );
        return { ...el, child: filteredChild };
      });

    } else {
      fields = allOptionFields;
    }

    setFilterOptionFields(fields);
  }, [searchKeyword, allOptionFields]);

  //自訂轉換欄(顯示分類)
  const customDisplayFields = Array.from(new Set(origiCustomData
    .map((s) => s.cidname)))
    .map((lab, index) => {
      const lastField = selectedOptionFields[selectedOptionFields.length - 1];
      return {
        category: lab,
        name: "自訂轉換欄",
        child: origiCustomData.filter((item) => item.cidname === lab)
          .map((edition, index) => {
            return {
              name: edition.name,
              expression: edition.id,
              format: 'DEFAULT',
              type: 'CUSTOM',
              indexNumber: edition?.indexNumber ? edition?.indexNumber : maxFieldsIndexNumber + 1 + index,
              sheet: excelSheetSelected,
              cidname: edition.cidname,
              cid: edition.cid,
            }
          })
      }
    })

  const handleTitle = (field) => {
    const allFields = _.flatMap(optionalFields.concat(customDisplayFields), f => f.child);

    const transformExpression = transformExpressToTw(field.expression, allFields, allFields);
    const format = field.format === 'PERCENT' ? '百分比' : field.format === 'DEFAULT' ? '數字' : '$';

    return `${transformExpression} (${format})`;
  }

  const changeOptionFields = (e, field) => {
    const { checked } = e.target;

    setSelectedOptionFields(prev =>
      checked
        ? [
          ...prev, {
            ...field,
            sheet: excelSheetSelected,
          }
        ]
        : prev.filter(el => el.expression !== field.expression)
    )
  }

  const changeAllConvertionsFields = (e, fields) => {
    const { checked } = e.target;
    const allConvertionsFields = fields.map((el) => ({
      ...el,
      sheet: excelSheetSelected
    }))

    setSelectedOptionFields(prev =>
      checked
        ? [...prev, ...allConvertionsFields.filter(newField =>
          !prev.some(existingField => existingField.expression === newField.expression)
        )]
        : prev.filter(existingField =>
          !allConvertionsFields.some(newField => newField.expression === existingField.expression)
        )
    )
  }

  const handleDeleteFields = (e, field, type) => {
    setCurrenOptionFields(null);

    const cancelFields = selectedOptionFields.filter(el => el.name !== field.name)
    setSelectedOptionFields([...cancelFields]);

    if (type === 'deletefield') {
      const updateFields = allOptionFields.map(el => ({
        ...el,
        child: el.category === 'custom' ? el.child.filter(child => child.expression !== field.expression) : el.child
      }));
      setAllOptionFields(updateFields);
    }
  }

  return (
    <Box mb={1}>
      <Typography
        sx={{
          ...theme.typography.h4_normal,
          mb: 2,
          color: theme.palette.grey.w700,
        }}
      >
        <PagesOutlinedIcon sx={{ mr: 0.5, verticalAlign: 'sub' }} />
        設定欄位指標
      </Typography>

      <Box mb={2}>
        <Stack variant='between-center'>
          <CustomFormLabel>欄位指標<span>*</span>
          </CustomFormLabel>

          <TaboolaFieldsCustomConvertions
            allOptionFields={allOptionFields}
            setAllOptionFields={setAllOptionFields}
            customDisplayFields={customDisplayFields}
            origiCustomData={origiCustomData}
            setOrigiCustomData={setOrigiCustomData}
            setCurrenOptionFields={setCurrenOptionFields}
            selectedAdAccounts={selectedAdAccounts}
            handleCustomData={handleCustomData}
            fetchData={fetchData}
          />
        </Stack>

        <BoxBorderGrey sx={{ display: 'flex', padding: 0 }} >
          <TaboolaFieldsAllMetric
            filterOptionFields={filterOptionFields}
            selectedOptionFields={selectedOptionFields}
            searchKeyword={searchKeyword}
            setSearchKeyword={setSearchKeyword}
            handleModalDetail={handleModalDetail}
            handleTitle={handleTitle}
            changeOptionFields={changeOptionFields}
            changeAllConvertionsFields={changeAllConvertionsFields}
            handleDeleteFields={handleDeleteFields}
          />

          <TaboolaFieldsSelected
            allOptionFields={allOptionFields}
            selectedOptionFields={selectedOptionFields}
            setSelectedOptionFields={setSelectedOptionFields}
            currenOptionFields={currenOptionFields}
            setCurrenOptionFields={setCurrenOptionFields}
            excelSheetSelected={excelSheetSelected}
            updateSortFields={updateSortFields}
            setUpdateSortFields={setUpdateSortFields}
            handleDeleteFields={handleDeleteFields}
          />
        </BoxBorderGrey>
        {
          <FormHelperText error>
            {inputErrorMsg.optionFieldName}
            {inputErrorMsg.selectFields}
            {downloadInputErrorMsg.optionFieldName}
            {downloadInputErrorMsg.selectFields}
          </FormHelperText>
        }
      </Box>
    </Box>
  )
}

export default TaboolaFieldsData;